import { useState, useCallback, useEffect } from 'react';
import './i18n';
import './App.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WishlistProvider, useWishlist } from './context/WishlistContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { 
  Navbar, 
  Hero, 
  Features, 
  ProductGrid, 
  HowItWorks, 
  FeaturedDestinations,
  CartDrawer, 
  Footer 
} from './components/Home';
import { CheckoutPage, OrderSuccess, PaymentStatus } from './components/Checkout';
import { ComparePage, CompareBar, useCompare } from './components/Compare';
import { InteractiveMap } from './components/InteractiveMap';
import { CountryPlansPopup } from './components/InteractiveGlobe';
import { RoamingCalculator } from './components/RoamingCalculator';
import ProductDetail from './components/ProductDetail';
import TravelHero from './components/TravelHero';
import { NetworkAlertBanner } from './components/NetworkAlertBanner';
import SetPassword from './components/SetPassword';
import { SocialProofToast } from './components/SocialProofToast';
import DealBanner from './components/DealBanner';
import { LoginPage, AuthCallback } from './components/AuthPage';
import AccountDashboard from './components/AccountDashboard';
import DeviceCompatibilityPage from './components/DeviceCompatibility';
import CookieBanner from './components/CookieBanner';
import ChatBot from './components/ChatBot';
import AboutPage from './components/AboutPage';
import { TermsPage, PrivacyPage, RefundPage } from './components/LegalPages';
import Testimonials from './components/Testimonials';
import GeoCurrencyBanner from './components/GeoCurrencyBanner';
import ReviewForm from './components/ReviewForm';
import AdminPanel from './components/AdminPanel';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import TripTracker from './components/TripTracker';
import TravelerActivate from './components/TravelerActivate';
import WhyeSIM4You from './components/WhyeSIM4You';
import PublicDiary from './components/PublicDiary';
import Translator from './components/Translator';
import TripPlanner from './components/TripPlanner';
import { Toaster, toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { trackAddToCart, trackBeginCheckout, trackPageView } from './utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function AppContent() {
  const [currentPage, setCurrentPage] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [stripeSessionId, setStripeSessionId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviewToken, setReviewToken] = useState(null);
  const { user } = useAuth();

  // Check URL for Stripe payment return or Google OAuth callback
  useEffect(() => {
    // Google OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setCurrentPage('auth-callback');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    const reviewParam = params.get('review');
    const refParam = params.get('ref');
    
    // Store referral code for later use during registration
    if (refParam) {
      localStorage.setItem('referral_code', refParam);
      // Track the click to notify the referrer
      const visitorId = localStorage.getItem('visitor_id') || `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem('visitor_id', visitorId);
      axios.post(`${API}/referral/track-click`, {
        referral_code: refParam,
        visitor_id: visitorId
      }).catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }

    // URL-based page routing
    const path = window.location.pathname;

    // Set password page
    if (params.get('page') === 'set-password' || path === '/set-password') {
      setCurrentPage('set-password');
    }

    // Admin panel access
    if (params.get('admin') !== null || path === '/admin') {
      setCurrentPage('admin');
    }
    if (path === '/shop') setCurrentPage('shop');
    else if (path === '/map') setCurrentPage('map');
    else if (path === '/compatibility') setCurrentPage('compatibility');
    else if (path === '/about') setCurrentPage('about');
    else if (path === '/how-it-works') setCurrentPage('how-it-works');
    else if (path === '/faq') setCurrentPage('faq');
    else if (path === '/contact') setCurrentPage('contact');
    else if (path === '/account') setCurrentPage('account');
    else if (path === '/savings') setCurrentPage('savings');

    // Trip tracking page (public)
    if (params.get('page') === 'track' && params.get('id')) {
      setCurrentPage('track');
    }

    // Traveler GPS activation page (public)
    if (params.get('page') === 'activate-tracking' && params.get('id') && params.get('token')) {
      setCurrentPage('activate-tracking');
    }

    // Public diary page
    if (params.get('page') === 'diary' && params.get('id')) {
      setCurrentPage('public-diary');
    }

    // Translator page
    if (params.get('page') === 'translator' || path === '/translator') {
      setCurrentPage('translator');
    }

    // Trip Planner page
    if (params.get('page') === 'trip-planner' || path === '/trip-planner') {
      setCurrentPage('trip-planner');
    }

    if (reviewParam) {
      setReviewToken(reviewParam);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (sessionId && payment === 'success') {
      setStripeSessionId(sessionId);
      setCurrentPage('payment-status');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      setCurrentPage('checkout');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  
  const { 
    cart, 
    cartCount, 
    cartTotal, 
    isOpen, 
    setIsOpen, 
    addToCart, 
    removeFromCart, 
    updateQuantity 
  } = useCart();

  const {
    compareProducts,
    toggleCompare,
    removeFromCompare,
    clearCompare,
    isInCompare
  } = useCompare();

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?per_page=500`);
        setProducts(response.data.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  const handleNavigate = useCallback((page) => {
    setCurrentPage(page);
    setSelectedCountry(null);
    window.scrollTo(0, 0);
    // Update URL path for SEO and direct linking
    const pathMap = { home: '/', shop: '/shop', map: '/map', compatibility: '/compatibility', about: '/about', faq: '/faq', contact: '/contact', account: '/account', savings: '/savings', terms: '/terms', privacy: '/privacy', refund: '/refund', 'how-it-works': '/how-it-works', 'trip-planner': '/trip-planner' };
    const newPath = pathMap[page] || '/';
    if (window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath);
    }
    // SEO: Update page title
    const titles = {
      home: 'eSIM4You - eSIM Data Plans for International Travel',
      shop: 'Shop eSIM Plans - eSIM4You',
      map: 'Destination Map - eSIM4You',
      compatibility: 'eSIM Compatibility Checker - eSIM4You',
      about: 'About Us - eSIM4You',
      terms: 'Terms & Conditions - eSIM4You',
      privacy: 'Privacy Policy - eSIM4You',
      refund: 'Refund Policy - eSIM4You',
      faq: 'FAQ - eSIM4You',
      contact: 'Contact Us - eSIM4You',
      checkout: 'Checkout - eSIM4You',
      account: 'My Account - eSIM4You',
    };
    document.title = titles[page] || 'eSIM4You';
    trackPageView(titles[page] || page);
  }, []);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage('shop');
  }, []);

  const handleAddToCart = useCallback(async (product) => {
    const success = await addToCart(product);
    if (success) {
      trackAddToCart(product);
      toast.success(`${product.name} added to cart!`, {
        style: {
          background: '#0f172a',
          border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff'
        }
      });
    } else {
      toast.error('Failed to add to cart. Please try again.', {
        style: {
          background: '#0f172a',
          border: '1px solid rgba(239,68,68,0.3)',
          color: '#fff'
        }
      });
    }
  }, [addToCart]);

  const handleCheckout = useCallback(() => {
    setIsOpen(false);
    setCurrentPage('checkout');
    trackBeginCheckout(cart.items, cartTotal);
  }, [setIsOpen, cart.items, cartTotal]);

  const handleOrderSuccess = useCallback((data) => {
    setOrderData(data);
    setCurrentPage('order-success');
  }, []);

  const handleToggleCompare = useCallback((product) => {
    toggleCompare(product);
    const isNowInCompare = !isInCompare(product.id);
    if (isNowInCompare) {
      toast.success(`${product.name} added to compare`, {
        icon: '⚖️',
        style: {
          background: '#0f172a',
          border: '1px solid rgba(6,182,212,0.3)',
          color: '#fff'
        }
      });
    }
  }, [toggleCompare, isInCompare]);

  const handleCompareNow = useCallback(() => {
    setCurrentPage('compare');
  }, []);

  const handleCountrySelect = useCallback((country) => {
    setSelectedCountry(country);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'compare':
        return (
          <ComparePage 
            selectedProducts={compareProducts}
            onBack={() => handleNavigate('shop')}
            onAddToCart={handleAddToCart}
            onAddMore={() => handleNavigate('shop')}
          />
        );
      
      case 'checkout':
        return (
          <CheckoutPage 
            onSuccess={handleOrderSuccess}
            onBack={() => handleNavigate('home')}
            onNavigate={handleNavigate}
          />
        );
      
      case 'order-success':
        return (
          <OrderSuccess 
            orderData={orderData}
            onContinue={() => handleNavigate('home')}
          />
        );
      
      case 'payment-status':
        return (
          <PaymentStatus 
            sessionId={stripeSessionId}
            onContinue={() => handleNavigate('home')}
            onBack={() => handleNavigate('checkout')}
          />
        );
      
      case 'calculator':
        return (
          <>
            <RoamingCalculator 
              products={products}
              onAddToCart={handleAddToCart}
            />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'map':
        return (
          <>
            <InteractiveMap 
              products={products}
              onAddToCart={handleAddToCart}
              onCountrySelect={handleCountrySelect}
            />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'shop':
        return (
          <>
            <div className="pt-16" />
            <ProductGrid 
              onAddToCart={handleAddToCart}
              searchQuery={searchQuery}
              onToggleCompare={handleToggleCompare}
              isInCompare={isInCompare}
              onProductClick={(product) => {
                setSelectedProduct(product);
                setCurrentPage('product-detail');
                window.scrollTo(0, 0);
              }}
            />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'product-detail':
        return selectedProduct ? (
          <>
            <ProductDetail
              product={selectedProduct}
              onBack={() => handleNavigate('shop')}
              onAddToCart={handleAddToCart}
            />
            <Footer onNavigate={handleNavigate} />
          </>
        ) : null;
      
      case 'how-it-works':
        return (
          <>
            <div className="pt-16" />
            <HowItWorks />
            <Features />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'compatibility':
        return (
          <>
            <DeviceCompatibilityPage />
            <Footer onNavigate={handleNavigate} />
          </>
        );

      case 'terms':
        return (
          <>
            <TermsPage onBack={() => handleNavigate('home')} />
            <Footer onNavigate={handleNavigate} />
          </>
        );

      case 'privacy':
        return (
          <>
            <PrivacyPage onBack={() => handleNavigate('home')} />
            <Footer onNavigate={handleNavigate} />
          </>
        );

      case 'refund':
        return (
          <>
            <RefundPage onBack={() => handleNavigate('home')} />
            <Footer onNavigate={handleNavigate} />
          </>
        );

      case 'about':
        return (
          <>
            <AboutPage onBack={() => handleNavigate('home')} />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'faq':
        return (
          <>
            <FAQPage />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'contact':
        return (
          <>
            <ContactPage />
            <Footer onNavigate={handleNavigate} />
          </>
        );
      
      case 'login':
        return (
          <LoginPage
            onBack={() => handleNavigate('home')}
            onSuccess={() => handleNavigate('account')}
          />
        );
      
      case 'auth-callback':
        return (
          <AuthCallback
            onSuccess={() => handleNavigate('account')}
          />
        );
      
      case 'account':
        return user ? (
          <>
            <AccountDashboard
              onNavigate={handleNavigate}
              onProductClick={(product) => {
                setSelectedProduct(product);
                setCurrentPage('product-detail');
                window.scrollTo(0, 0);
              }}
            />
            <Footer onNavigate={handleNavigate} />
          </>
        ) : (
          <LoginPage
            onBack={() => handleNavigate('home')}
            onSuccess={() => handleNavigate('account')}
          />
        );
      
      case 'admin':
        return (
          <AdminPanel onBack={() => handleNavigate('home')} />
        );
      
      case 'set-password': {
        const token = new URLSearchParams(window.location.search).get('token');
        return (
          <SetPassword token={token} onComplete={() => handleNavigate('account')} />
        );
      }

      case 'track': {
        const trackId = new URLSearchParams(window.location.search).get('id');
        return (
          <TripTracker shareId={trackId} onBack={() => handleNavigate('home')} />
        );
      }

      case 'activate-tracking': {
        const params = new URLSearchParams(window.location.search);
        return (
          <TravelerActivate shareId={params.get('id')} travelerToken={params.get('token')} />
        );
      }

      case 'public-diary': {
        const diaryId = new URLSearchParams(window.location.search).get('id');
        return (
          <PublicDiary shareId={diaryId} onBack={() => handleNavigate('home')} onNavigate={handleNavigate} />
        );
      }

      case 'translator':
        return (
          <Translator onBack={() => handleNavigate('home')} />
        );

      case 'trip-planner':
        return (
          <TripPlanner onNavigate={handleNavigate} />
        );
      
      default:
        return (
          <>
            <TravelHero
              totalProducts={products.length}
              onSearch={handleSearch}
              onNavigate={handleNavigate}
            />
            <div className="max-w-6xl mx-auto px-4 mt-4">
              <NetworkAlertBanner />
            </div>
            <HowItWorks />
            <FeaturedDestinations
              products={products}
              onProductClick={(product) => {
                setSelectedProduct(product);
                setCurrentPage('product-detail');
                window.scrollTo(0, 0);
              }}
              onAddToCart={handleAddToCart}
              onToggleCompare={handleToggleCompare}
              isInCompare={isInCompare}
              onViewAll={() => handleNavigate('shop')}
            />
            <Features />
            <WhyeSIM4You onNavigate={handleNavigate} />
            <Testimonials />
            <Footer onNavigate={handleNavigate} />
          </>
        );
    }
  };

  return (
    <div className="App bg-[#0f1419] min-h-screen">
      <Navbar 
        cartCount={cartCount}
        onCartClick={() => setIsOpen(true)}
        onNavigate={handleNavigate}
        currentPage={currentPage}
      />
      <div className="pt-16">
        <DealBanner onViewDeal={(country) => {
          setSearchQuery(country);
          setCurrentPage('shop');
        }} />
        <GeoCurrencyBanner />
      </div>
      
      {renderPage()}
      
      <CartDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        cart={cart}
        onRemove={removeFromCart}
        onUpdateQuantity={updateQuantity}
        cartTotal={cartTotal}
        onCheckout={handleCheckout}
      />

      <AnimatePresence>
        {compareProducts.length > 0 && currentPage !== 'compare' && (
          <CompareBar
            selectedProducts={compareProducts}
            onRemove={removeFromCompare}
            onCompare={handleCompareNow}
            onClear={clearCompare}
          />
        )}
      </AnimatePresence>

      {/* Country popup for map page */}
      <AnimatePresence>
        {selectedCountry && currentPage === 'map' && (
          <CountryPlansPopup
            country={selectedCountry}
            products={products}
            onClose={() => setSelectedCountry(null)}
            onAddToCart={(product) => {
              handleAddToCart(product);
              setSelectedCountry(null);
            }}
          />
        )}
      </AnimatePresence>
      
      <Toaster position="bottom-right" />
      {reviewToken && (
        <ReviewForm
          token={reviewToken}
          onClose={() => setReviewToken(null)}
        />
      )}
    </div>
  );
}

function FAQPage() {
  const faqs = [
    {
      q: "What is an eSIM?",
      a: "An eSIM (embedded SIM) is a digital SIM that allows you to activate a cellular plan without using a physical SIM card. It's built into your device."
    },
    {
      q: "Which devices support eSIM?",
      a: "Most modern smartphones support eSIM, including iPhone XS and newer, Samsung Galaxy S20 and newer, Google Pixel 3 and newer, and many more."
    },
    {
      q: "How do I install the eSIM?",
      a: "After purchase, you'll receive a QR code via email. Simply scan it with your phone's camera or go to Settings > Cellular > Add eSIM and scan the code."
    },
    {
      q: "Can I use my eSIM in multiple countries?",
      a: "It depends on the plan. Our regional and global plans work in multiple countries. Single-country plans work only in the specified country."
    },
    {
      q: "What happens when my data runs out?",
      a: "You can purchase a new eSIM plan or top-up if available. Your existing eSIM will remain on your device until you delete it."
    },
    {
      q: "Is there a refund policy?",
      a: "Yes, we offer refunds for unused eSIMs within 7 days of purchase. Once activated, eSIMs cannot be refunded."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-12 font-outfit text-center">
          Frequently Asked Questions
        </h1>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details
              key={index}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden group"
            >
              <summary className="px-6 py-4 cursor-pointer text-white font-medium hover:bg-white/5 transition-colors list-none flex justify-between items-center">
                {faq.q}
                <span className="text-teal-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-6 pb-4 text-slate-400">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8 font-outfit text-center">
          Contact Us
        </h1>
        
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl text-white mb-2">Message Sent!</h3>
              <p className="text-slate-400">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  required
                  rows={5}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                  data-testid="contact-message"
                />
              </div>
              
              {status === 'error' && (
                <div className="text-rose-400 text-sm">Something went wrong. Please try again.</div>
              )}
              
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 text-black font-bold py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]"
                data-testid="contact-submit"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  const { i18n } = useTranslation();
  return (
    <AuthProvider>
      <WishlistProvider>
        <CurrencyProvider language={i18n.language}>
          <CartProvider>
            <AppContent />
            <SocialProofToast />
            <CookieBanner />
            <ChatBot />
            <PWAInstallPrompt />
          </CartProvider>
        </CurrencyProvider>
      </WishlistProvider>
    </AuthProvider>
  );
}

export default App;
